class ResourcesIndex extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-resources-grid]');
    this.list = this.querySelector('[data-resources-list]');
    this.countEl = this.querySelector('[data-resources-count]');
    this.searchInput = this.querySelector('[data-resources-search]');
    this.typeFilter = this.querySelector('[data-resources-type-filter]');
    this.typeFilterWrap = this.querySelector('[data-resources-type-filter-wrap]');
    this.typeControl = this.querySelector('.resources-index__type-control');
    this.placeholderSrc = this.dataset.placeholderSrc || '';
    this.viewButtons = Array.from(this.querySelectorAll('[data-resources-view]'));
    this.pagination = this.querySelector('[data-resources-pagination]');
    this.gridTemplate = this.querySelector('[data-resources-grid-template]');
    this.listTemplate = this.querySelector('[data-resources-list-template]');
    this.emptyEl = this.querySelector('[data-resources-empty]');
    this.countLabel = this.dataset.countLabel || 'Files';
    this.downloadLabel = this.dataset.downloadLabel || 'Download';
    this.pageSize = Number(this.dataset.pageSize) || 20;
    this.currentPage = Number(this.dataset.currentPage) || 1;
    this.sortCataloguesFirst = this.dataset.cataloguesFirst === 'true';
    this.ajaxUrl = this.dataset.ajaxUrl || '';
    this.showTypeFilter = this.dataset.showTypeFilter === 'true';
    this.totalCount = Number(this.dataset.totalCount) || 0;
    this.index = null;
    this.indexPromise = null;
    this.usingClientIndex = false;
    this.view = 'grid';
    this.searchTimer = null;
    this.prevIcon = this.pagination?.querySelector('.resources-index__page-arrow-img--prev')?.getAttribute('src') || '';
    this.nextIcon = this.pagination?.querySelector('.resources-index__page-arrow-img:not(.resources-index__page-arrow-img--prev)')?.getAttribute('src') || '';

    this.setCount(this.totalCount);

    if (!this.grid) return;

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => this.onFilterChange(), 200);
      });
    }

    if (this.typeFilter) {
      this.typeFilter.addEventListener('change', () => {
        this.syncTypeFilterState();
        this.onFilterChange();
      });
      this.syncTypeFilterState();
    }

    this.viewButtons.forEach((button) => {
      button.addEventListener('click', () => this.setView(button.dataset.resourcesView));
    });

    if (this.pagination) {
      this.pagination.addEventListener('click', (event) => {
        if (!this.usingClientIndex) return;
        const control = event.target.closest('[data-page]');
        if (!control) return;
        event.preventDefault();
        const page = Number(control.dataset.page);
        if (!page || page === this.currentPage) return;
        this.currentPage = page;
        this.renderFromIndex();
      });
    }

    this.loadIndex();
    this.hydrateFileSizes();
  }

  setCount(count) {
    if (this.countEl) {
      this.countEl.textContent = `${count} ${this.countLabel}`;
    }
  }

  setView(view) {
    if (!view) return;
    this.view = view;
    if (this.grid) {
      this.grid.dataset.view = view === 'list' ? 'list' : 'grid';
      this.grid.hidden = view !== 'grid';
    }
    if (this.list) this.list.hidden = view !== 'list';
    this.viewButtons.forEach((button) => {
      const active = button.dataset.resourcesView === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  query() {
    return (this.searchInput?.value || '').trim().toLowerCase();
  }

  typeValue() {
    return (this.typeFilter?.value || '').trim().toLowerCase();
  }

  hasClientFilter() {
    return this.query() !== '' || this.typeValue() !== '';
  }

  async onFilterChange() {
    this.currentPage = 1;
    if (this.index) {
      this.renderFromIndex();
      return;
    }

    try {
      await this.ensureIndex();
      this.renderFromIndex();
    } catch (error) {
      this.filterDomCards();
    }
  }

  async loadIndex() {
    this.indexPromise = this.fetchAllArticles();
    try {
      this.index = this.normalizeIndex(await this.indexPromise);
      this.populateTypeFilter(this.index);
      this.setCount(this.index.length || this.totalCount);
      if (this.sortCataloguesFirst || this.hasClientFilter()) {
        this.renderFromIndex();
      } else {
        this.hydrateFileSizes();
      }
    } catch (error) {
      this.index = null;
      this.populateTypeFilter(this.domItems());
    }
  }

  async ensureIndex() {
    if (this.index) return this.index;
    if (!this.indexPromise) {
      this.indexPromise = this.fetchAllArticles();
    }
    this.index = this.normalizeIndex(await this.indexPromise);
    this.populateTypeFilter(this.index);
    return this.index;
  }

  normalizeIndex(items) {
    return (items || []).map((item) => {
      const format = this.itemFormat(item);
      return {
        ...item,
        format,
        type: format || item.type || '',
      };
    });
  }

  itemFormat(item) {
    const raw = String(item?.format || '').trim();
    if (raw && !this.isCategoryLabel(raw)) return this.normalizeExt(raw);
    return this.formatFromUrl(item?.resourceUrl || item?.url || '');
  }

  isCategoryLabel(value) {
    const label = String(value || '').toLowerCase();
    return (
      label.includes('catalogue')
      || label.includes('technical')
      || label.includes('bim')
    );
  }

  normalizeExt(value) {
    const ext = String(value || '').trim().toUpperCase();
    if (ext === 'JPEG') return 'JPG';
    return ext;
  }

  formatFromUrl(url) {
    const path = decodeURIComponent(String(url || '').split('?')[0].split('#')[0]);
    if (!path || path.includes('/blogs/')) return '';
    const name = path.split('/').pop() || '';
    const dot = name.lastIndexOf('.');
    if (dot < 0) return '';
    const ext = name.slice(dot + 1).toLowerCase();
    if (!/^[a-z0-9]{2,5}$/.test(ext)) return '';
    return this.normalizeExt(ext);
  }

  domItems() {
    return Array.from(this.grid?.querySelectorAll('[data-resources-card]') || []).map((card) => ({
      id: card.dataset.resourceId,
      title: card.querySelector('.resources-index-card__title')?.textContent.trim() || '',
      searchText: card.dataset.searchText || '',
      format: card.dataset.resourceFormat || '',
      resourceUrl: card.dataset.resourceUrl || '',
      category: card.dataset.resourceCategory || '',
    }));
  }

  async fetchAllArticles() {
    if (!this.ajaxUrl) throw new Error('Missing ajax url');
    const first = await this.fetchPage(1);
    const articles = [...(first.articles || [])];
    const pages = Number(first.pages) || 1;
    if (pages > 1) {
      const rest = await Promise.all(Array.from({ length: pages - 1 }, (_, index) => this.fetchPage(index + 2)));
      rest.forEach((page) => {
        articles.push(...(page.articles || []));
      });
    }
    if (this.sortCataloguesFirst) {
      articles.sort((firstItem, secondItem) => this.catalogueRank(firstItem) - this.catalogueRank(secondItem));
    }
    return articles;
  }

  catalogueRank(item) {
    const category = String(item.category || '').toLowerCase();
    const tags = Array.isArray(item.tags) ? item.tags.map((tag) => String(tag).toLowerCase()) : [];
    if (category.includes('catalogue') || tags.some((tag) => tag.includes('catalogue'))) return 0;
    return 1;
  }

  async fetchPage(page) {
    const url = new URL(this.ajaxUrl, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    });
    url.searchParams.set('view', 'resources.ajax');
    url.searchParams.set('page', String(page));
    const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Failed to load resources');
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (error) {
      const start = text.indexOf('{');
      const end = text.lastIndexOf('}');
      if (start === -1 || end === -1) throw new Error('Invalid resources response');
      return JSON.parse(text.slice(start, end + 1));
    }
  }

  matches(item, query, type) {
    const haystack = `${item.searchText || ''} ${item.title || ''}`.toLowerCase();
    const format = this.itemFormat(item).toLowerCase();
    return (!query || haystack.includes(query)) && (!type || format === type);
  }

  renderFromIndex() {
    if (!this.index) return;
    this.usingClientIndex = true;
    const query = this.query();
    const type = this.typeValue();
    const matches = this.index.filter((item) => this.matches(item, query, type));
    this.setCount(matches.length);

    const pages = Math.max(1, Math.ceil(matches.length / this.pageSize));
    if (this.currentPage > pages) this.currentPage = pages;
    const start = (this.currentPage - 1) * this.pageSize;
    const visible = matches.slice(start, start + this.pageSize);
    this.renderCards(visible);
    this.renderPagination(this.currentPage, pages);
    this.toggleEmpty(matches.length === 0);
    this.hydrateFileSizes();
  }

  renderCards(items) {
    if (this.grid && this.gridTemplate) {
      this.grid.replaceChildren(...items.map((item) => this.createGridCard(item)));
      this.grid.hidden = this.view !== 'grid';
    }
    if (this.list && this.listTemplate) {
      this.list.replaceChildren(...items.map((item) => this.createListCard(item)));
      this.list.hidden = this.view !== 'list';
    }
  }

  createGridCard(item) {
    const card = this.gridTemplate.content.firstElementChild.cloneNode(true);
    card.dataset.searchText = item.searchText || item.title || '';
    card.dataset.resourceId = String(item.id || '');
    card.dataset.resourceFormat = this.itemFormat(item);
    card.dataset.resourceCategory = item.category || '';
    card.dataset.resourceUrl = item.resourceUrl || '';
    card.dataset.resourceSize = item.size || '';

    const media = card.querySelector('.resources-index-card__media');
    const img = card.querySelector('.resources-index-card__img');
    const placeholder = card.querySelector('.resources-index-card__placeholder-img');
    const resourceUrl = item.resourceUrl || item.url || '#';
    media.setAttribute('href', resourceUrl);
    this.applyDownload(media, item);

    if (item.image) {
      img.src = item.image;
      img.alt = item.title || '';
      placeholder?.remove();
    } else {
      img?.remove();
      if (placeholder && this.placeholderSrc) placeholder.src = this.placeholderSrc;
    }

    const titleLink = card.querySelector('.resources-index-card__title a');
    titleLink.setAttribute('href', item.url || resourceUrl);
    titleLink.textContent = item.title || '';
    this.renderMeta(card.querySelector('.resources-index-card__meta'), item);

    const button = card.querySelector('.resources-index-card__btn');
    button.setAttribute('href', resourceUrl);
    button.textContent = this.downloadLabel;
    this.applyDownload(button, item);
    return card;
  }

  createListCard(item) {
    const card = this.listTemplate.content.firstElementChild.cloneNode(true);
    const resourceUrl = item.resourceUrl || item.url || '#';
    card.dataset.searchText = item.searchText || item.title || '';
    card.dataset.resourceId = String(item.id || '');
    card.dataset.resourceFormat = this.itemFormat(item);
    card.dataset.resourceCategory = item.category || '';
    card.dataset.resourceUrl = item.resourceUrl || '';
    card.dataset.resourceSize = item.size || '';
    card.setAttribute('href', resourceUrl);
    this.applyDownload(card, item);
    card.querySelector('.system-download-card__title').textContent = item.title || '';
    this.renderMeta(card.querySelector('.system-download-card__meta'), item, true);
    return card;
  }

  renderMeta(container, item, useSystemClasses = false) {
    if (!container) return;
    const type = this.itemFormat(item) || item.type || '';
    const size = item.size || '';
    container.replaceChildren();
    if (type) {
      const typeEl = document.createElement('span');
      if (useSystemClasses) typeEl.className = 'system-download-card__format';
      typeEl.textContent = type;
      container.appendChild(typeEl);
    }
    if (size) {
      const sizeEl = document.createElement('span');
      if (useSystemClasses) sizeEl.className = 'system-download-card__size';
      sizeEl.dataset.resourceFilesize = '';
      sizeEl.textContent = size;
      container.appendChild(sizeEl);
    }
    container.hidden = !type && !size;
  }

  applyDownload(element, item) {
    if (!element) return;
    if (item.isFileDownload) {
      element.setAttribute('download', item.downloadName || item.title || '');
      element.setAttribute('target', '_blank');
      element.setAttribute('rel', 'noopener');
      return;
    }
    element.removeAttribute('download');
    element.removeAttribute('target');
    element.removeAttribute('rel');
  }

  renderPagination(page, pages) {
    if (!this.pagination) return;
    if (pages <= 1) {
      this.pagination.classList.add('is-hidden');
      this.pagination.replaceChildren();
      return;
    }

    this.pagination.classList.remove('is-hidden');
    const parts = [];
    parts.push(this.paginationArrow(page - 1, page === 1, true));
    for (let number = 1; number <= pages; number += 1) {
      if (number === page) {
        const current = document.createElement('span');
        current.className = 'resources-index__page resources-index__page--current';
        current.setAttribute('aria-current', 'page');
        current.textContent = String(number);
        parts.push(current);
      } else {
        const link = document.createElement('a');
        link.className = 'resources-index__page';
        link.href = '#';
        link.dataset.page = String(number);
        link.textContent = String(number);
        parts.push(link);
      }
    }
    parts.push(this.paginationArrow(page + 1, page === pages, false));
    this.pagination.replaceChildren(...parts);
  }

  paginationArrow(page, disabled, previous) {
    if (disabled) {
      const span = document.createElement('span');
      span.className = 'resources-index__page-arrow resources-index__page-arrow--disabled';
      span.setAttribute('aria-hidden', 'true');
      span.appendChild(this.paginationIcon(previous));
      return span;
    }
    const link = document.createElement('a');
    link.className = 'resources-index__page-arrow';
    link.href = '#';
    link.dataset.page = String(page);
    link.setAttribute('aria-label', previous ? 'Previous' : 'Next');
    link.appendChild(this.paginationIcon(previous));
    return link;
  }

  paginationIcon(previous) {
    const img = document.createElement('img');
    img.src = previous ? this.prevIcon : this.nextIcon;
    img.alt = '';
    img.width = 40;
    img.height = 40;
    img.className = previous
      ? 'resources-index__page-arrow-img resources-index__page-arrow-img--prev'
      : 'resources-index__page-arrow-img';
    return img;
  }

  populateTypeFilter(items) {
    if (!this.typeFilter || !this.showTypeFilter) return;
    const formats = [
      ...new Set(
        items
          .map((item) => this.itemFormat(item))
          .filter(Boolean)
      ),
    ].sort();

    const current = this.typeFilter.value;
    const allLabel = this.typeFilter.querySelector('option[value=""]')?.textContent || 'All';
    const allOption = document.createElement('option');
    allOption.value = '';
    allOption.textContent = allLabel;
    this.typeFilter.replaceChildren(allOption);
    formats.forEach((format) => {
      const option = document.createElement('option');
      option.value = format.toLowerCase();
      option.textContent = format;
      this.typeFilter.appendChild(option);
    });
    this.typeFilter.value = current;
    if (formats.length < 2) {
      this.typeFilterWrap?.setAttribute('hidden', '');
      return;
    }
    this.typeFilterWrap?.removeAttribute('hidden');
    this.syncTypeFilterState();
  }

  syncTypeFilterState() {
    this.typeControl?.classList.toggle('is-placeholder', !this.typeValue());
  }

  toggleEmpty(isEmpty) {
    const hasResults = !isEmpty;
    if (this.grid) this.grid.hidden = this.view !== 'grid' || isEmpty;
    if (this.list) this.list.hidden = this.view !== 'list' || isEmpty;
    if (this.emptyEl) this.emptyEl.hidden = hasResults;
  }

  filterDomCards() {
    const query = this.query();
    const type = this.typeValue();
    let visible = 0;
    this.querySelectorAll('[data-resources-card]').forEach((card) => {
      const haystack = (card.dataset.searchText || '').toLowerCase();
      const format = (card.dataset.resourceFormat || '').toLowerCase();
      const match = (!query || haystack.includes(query)) && (!type || format === type);
      card.classList.toggle('is-hidden', !match);
      if (match && card.closest('[data-resources-grid]')) visible += 1;
    });
    this.setCount(query || type ? visible : this.totalCount);
    this.hydrateFileSizes();
  }

  hydrateFileSizes() {
    const cards = Array.from(this.querySelectorAll('[data-resources-card]')).filter((card) => {
      if (card.dataset.resourceSize) return false;
      return Boolean(this.cardFileUrl(card));
    });
    cards.forEach((card) => {
      const url = this.cardFileUrl(card);
      this.fetchFileBytes(url).then((bytes) => {
        if (!bytes) return;
        const label = this.formatBytes(bytes);
        card.dataset.resourceSize = label;
        this.writeCardSize(card, label);
        const id = card.dataset.resourceId;
        if (this.index && id) {
          const item = this.index.find((entry) => String(entry.id) === id);
          if (item) item.size = label;
        }
      });
    });
  }

  cardFileUrl(card) {
    const url = card.dataset.resourceUrl
      || card.getAttribute('href')
      || card.querySelector('.resources-index-card__media')?.getAttribute('href')
      || card.querySelector('.resources-index-card__btn')?.getAttribute('href')
      || '';
    if (!url || url === '#' || url.includes('/blogs/')) return '';
    return url;
  }

  resolveFileUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  async fetchFileBytes(url) {
    const resolved = this.resolveFileUrl(url);
    if (!resolved) return 0;
    this.sizeCache = this.sizeCache || new Map();
    if (this.sizeCache.has(resolved)) return this.sizeCache.get(resolved);
    const storageKey = `resource-bytes:${resolved}`;
    try {
      const cached = window.sessionStorage.getItem(storageKey);
      if (cached) {
        const bytes = Number(cached);
        this.sizeCache.set(resolved, bytes);
        return bytes;
      }
    } catch (error) {
      // sessionStorage can be blocked
    }
    try {
      const response = await fetch(resolved, { method: 'HEAD', mode: 'cors' });
      const bytes = Number(response.headers.get('content-length'));
      if (!bytes) return 0;
      this.sizeCache.set(resolved, bytes);
      try {
        window.sessionStorage.setItem(storageKey, String(bytes));
      } catch (error) {
        // ignore quota / private mode
      }
      return bytes;
    } catch (error) {
      return 0;
    }
  }

  formatBytes(bytes) {
    if (!bytes) return '';
    const format = (value, unit) => `${value.toFixed(1).replace(/\.0$/, '')}${unit}`;
    if (bytes >= 1073741824) return format(bytes / 1073741824, 'GB');
    if (bytes >= 1048576) return format(bytes / 1048576, 'MB');
    if (bytes >= 1024) return format(bytes / 1024, 'KB');
    return `${bytes}B`;
  }

  writeCardSize(card, label) {
    if (card.matches('.system-download-card')) {
      const copy = card.querySelector('.system-download-card__copy');
      let meta = card.querySelector('.system-download-card__meta');
      if (!meta && copy) {
        meta = document.createElement('span');
        meta.className = 'system-download-card__meta';
        copy.appendChild(meta);
      }
      if (!meta) return;
      let sizeEl = meta.querySelector('[data-resource-filesize], .system-download-card__size');
      if (!sizeEl) {
        sizeEl = document.createElement('span');
        sizeEl.className = 'system-download-card__size';
        sizeEl.dataset.resourceFilesize = '';
        meta.appendChild(sizeEl);
      }
      sizeEl.textContent = label;
      return;
    }

    const copy = card.querySelector('.resources-index-card__copy');
    let meta = card.querySelector('.resources-index-card__meta');
    if (!meta && copy) {
      meta = document.createElement('p');
      meta.className = 'resources-index-card__meta';
      copy.appendChild(meta);
    }
    if (!meta) return;
    let sizeEl = meta.querySelector('[data-resource-filesize]');
    if (!sizeEl) {
      sizeEl = document.createElement('span');
      sizeEl.dataset.resourceFilesize = '';
      meta.appendChild(sizeEl);
    }
    sizeEl.textContent = label;
  }
}

customElements.define('resources-index', ResourcesIndex);
